import React from 'react';
import {
  AccountInfoResult,
  DeployContractPackageResult,
  FTEntryPointType,
  FTTransactionResult,
  TransactorHashType,
} from '@block-explorer/entities';
import { BodyText, FlexRow } from '@make-software/cspr-design';
import Cep18FormattedAmount from '../../../components/cep18-formatted-amount/cep18-formatted-amount';
import { PrecisionCase } from '../../../utils/currency';
import {
  ContractIdentifier,
  HashFontSize,
  TransactorInfo,
} from '../../../components';
import { useDeployActionDataContext } from '../services/deploy-action-context';
import { X402Badge } from '../../action-messages/x402-badge';

// TODO: FIX type for Cep18ActionRow
// @ts-ignore
interface Cep18ActionRowProps extends FTTransactionResult {
  actionName: string | undefined;
  prefix?: string | null;
  senderPrefix?: string | null;
  amount?: string;
  contractPackage: DeployContractPackageResult;
  from_hash?: string;
  from_public_key?: string | null;
  from_type?: TransactorHashType;
  to_hash?: string;
  to_public_key?: string | null;
  to_type?: TransactorHashType;
  entryPointName?: string;
}

export const Cep18ActionRow = ({
  amount,
  contractPackage,
  from_public_key,
  from_hash,
  from_type,
  to_hash,
  to_type,
  to_public_key,
  actionName,
  prefix,
  senderPrefix,
  to_account_info,
  from_account_info,
  to_centralized_account_info,
  from_centralized_account_info,
  entryPointName,
}: Cep18ActionRowProps) => {
  const { getAccountInfo } = useDeployActionDataContext();
  const fromAccountInfo = getAccountInfo<AccountInfoResult>(
    from_public_key || from_hash || ''
  );
  const toAccountInfo = getAccountInfo<AccountInfoResult>(
    to_public_key || to_hash || ''
  );
  const isX402Transaction =
    entryPointName === FTEntryPointType.transfer_with_authorization;

  return (
    <FlexRow align={'center'} itemsSpacing={8}>
      {isX402Transaction && <X402Badge />}
      <BodyText monotype size={3} wordBreak noWrap variation={'black'}>
        {actionName}
      </BodyText>
      {prefix && (
        <BodyText size={3} noWrap variation={'darkGray'}>
          {prefix}
        </BodyText>
      )}
      {amount && (
        <BodyText size={3} monotype variation={'black'}>
          <Cep18FormattedAmount
            amount={amount}
            precisionCase={PrecisionCase.deployCost}
            contractPackage={contractPackage}
          />
        </BodyText>
      )}
      <FlexRow align={'center'} itemsSpacing={2}>
        <BodyText size={3} noWrap variation={'darkGray'}>
          (
        </BodyText>
        <ContractIdentifier
          hash={contractPackage?.contract_package_hash}
          avatarSize={'small'}
          hashFontSize={HashFontSize.big}
          contractPackage={contractPackage}
          hideContractType
        />
        <BodyText size={3} noWrap variation={'darkGray'}>
          ) token(s)
        </BodyText>
      </FlexRow>

      {from_hash && (
        <>
          <BodyText size={3} noWrap variation={'darkGray'}>
            {senderPrefix}
          </BodyText>
          <TransactorInfo
            type={from_type}
            accountInfo={
              fromAccountInfo?.account_info ||
              fromAccountInfo?.centralized_account_info ||
              from_centralized_account_info ||
              from_account_info
            }
            csprName={fromAccountInfo?.cspr_name}
            publicKey={from_public_key}
            hashFontSize={HashFontSize.big}
            avatarSize="small"
            hash={from_hash}
            loading={false}
          />
        </>
      )}
      {to_hash && (
        <>
          <BodyText size={3} noWrap variation={'darkGray'}>
            to
          </BodyText>
          <TransactorInfo
            type={to_type}
            accountInfo={
              toAccountInfo?.account_info ||
              toAccountInfo?.centralized_account_info ||
              to_centralized_account_info ||
              to_account_info
            }
            csprName={toAccountInfo?.cspr_name}
            publicKey={to_public_key}
            hashFontSize={HashFontSize.big}
            avatarSize="small"
            hash={to_hash}
            loading={false}
          />
        </>
      )}
    </FlexRow>
  );
};
